/**
 * App Providers
 *
 * Composite provider that wraps the entire application.
 * Order matters: QueryClient > Auth > Ability > Router
 *
 * @layer app
 * @segment providers
 * @added DOC-0100 (2025-12-26)
 */

import type { ReactNode } from 'react';
import { StrictMode } from 'react';
import { QueryProvider } from './QueryProvider';
import { AuthProvider } from '@features/auth';
import { AbilityProvider } from '@features/authorization';
import { RouterProviderWithContext } from './RouterProvider';

interface AppProvidersProps {
  children?: ReactNode;
}

/**
 * AppProviders - Composite provider for the application
 *
 * Provider hierarchy:
 * 1. StrictMode - React strict mode for development
 * 2. QueryProvider - TanStack Query client
 * 3. AuthProvider - Authentication state
 * 4. AbilityProvider - Authorization abilities
 * 5. RouterProviderWithContext - TanStack Router
 *
 * Note: RouterProviderWithContext is included here and renders
 * the router, so children are typically not needed.
 */
export function AppProviders({ children }: AppProvidersProps) {
  return (
    <StrictMode>
      <QueryProvider>
        <AuthProvider>
          <AbilityProvider>{children ?? <RouterProviderWithContext />}</AbilityProvider>
        </AuthProvider>
      </QueryProvider>
    </StrictMode>
  );
}
